import React, { forwardRef } from "react";
import shouldForwardProp from "@emotion/is-prop-valid";
import { Skeleton } from "../Skeleton/Skeleton.tsx";
import { css } from "@emotion/react";
import { fontWeight, variant, textAlign } from "styled-system";
import styled from "@emotion/styled";
import { SpinnerSpinningMini as Spinner } from "src/lib/ui-icon-v2";

import type {
  FontWeightProps,
  TextAlignProps,
  ThemeValue,
  MarginProps,
} from "styled-system";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import type { ButtonHTMLAttributes, ClassAttributes, ReactNode } from "react";
import { TextV2 } from "src/components/TextV2/TextV2.tsx";

const variants = {
  outlined: {
    borderWidth: "1px",
    borderStyle: "solid",
    borderRadius: "6px",
  },
  filled: {
    border: "none",
    borderRadius: "6px",
  },
};

const sizes = {
  sm: {
    height: 37,
    minWidth: 34,
    paddingLeft: "10px",
    paddingRight: "10px",
  },
  md: {
    height: 45,
    minWidth: 40,
    paddingLeft: "15px",
    paddingRight: "15px",
  },
};

type Skin = "info" | "danger" | "success" | "warning" | "neutral";
export type ButtonSkin = Skin;

const getNeutralButtonColorStyles = ({
  variant = "filled",
  disabled,
  theme,
}: {
  theme: ExactTheme;
  variant?: Variant;
  disabled?: boolean;
}) => {
  const { colorsV2 } = theme;
  return {
    borderColor: colorsV2.grey[300],
    borderStyle: "solid",
    borderWidth: "1px",
    transition: "150ms",
    background: variant === "outlined" ? "transparent" : colorsV2.grey[500],
    color: variant === "outlined" ? colorsV2.grey[800] : "white",
    [`.${classes.startAdornment}, .${classes.endAdornment}`]: {
      color: variant === "outlined" ? colorsV2.grey[500] : "white",
    },
    ":hover": {
      background: colorsV2.grey[50],
    },
    ":focus": {
      outline: "none",
    },
    ":focus-visible": {
      // focus-visible is triggered by keyboard
      [`.${classes.startAdornment}, .${classes.endAdornment}`]: {
        color: "white",
      },
      color: "white",
      background: colorsV2.grey[800],
      borderColor: colorsV2.grey[800],
      outline: `2px solid ${colorsV2.grey[100]}`,
    },
  };
};

const getButtonColorStyles = ({
  variant = "filled",
  skin = "info",
  disabled,
  theme,
}: {
  theme: ExactTheme;
  variant?: Variant;
  skin?: Skin;
  disabled?: boolean;
}) => {
  const { colorsV2 } = theme;

  if (disabled) {
    return {
      borderColor: colorsV2.grey[200],
      borderStyle: "solid",
      borderWidth: "1px",
      background: variant === "outlined" ? "transparent" : colorsV2.grey[200],
      color: colorsV2.grey[600],
    };
  }

  if (skin === "neutral") {
    return getNeutralButtonColorStyles({
      theme,
      disabled,
      variant,
    });
  }

  const colorName = (
    {
      info: "blue",
      success: "green",
      danger: "red",
      warning: "orange",
    } as const
  )[skin];

  return {
    borderColor: colorsV2[colorName][500],
    borderStyle: "solid",
    borderWidth: "1px",
    transition: "150ms",

    background:
      variant === "outlined" ? "transparent" : colorsV2[colorName][500],
    color: variant === "outlined" ? colorsV2.grey[800] : "white",
    [`.${classes.startAdornment}, .${classes.endAdornment}`]: {
      color: variant === "outlined" ? colorsV2[colorName][500] : "white",
    },
    ":hover": {
      [`.${classes.startAdornment}, .${classes.endAdornment}`]: {
        color: "white",
      },
      color: "white",
      background: colorsV2[colorName][600],
      borderColor: colorsV2[colorName][600],
    },
    ":focus": {
      outline: "none",
    },
    ":focus-visible": {
      // focus-visible is triggered by keyboard
      [`.${classes.startAdornment}, .${classes.endAdornment}`]: {
        color: "white",
      },
      color: "white",
      background: colorsV2[colorName][700],
      borderColor: colorsV2[colorName][700],
      outline: `2px solid ${colorsV2[colorName][100]}`,
    },
  };
};

const buttonTheme = {
  variants,
  sizes,
};

type Size = keyof (typeof buttonTheme)["sizes"];
type Variant = ThemeValue<"variants", typeof buttonTheme>;
export type ButtonVariant = Variant;

interface VariantProps {
  variant?: Variant;
}

interface SizeVariationProps {
  size?: Size;
}

type Theme = Omit<ExactTheme, "buttons">;

interface ButtonBaseProps
  extends Omit<ButtonHTMLAttributes<HTMLButtonElement>, "color">,
    ClassAttributes<HTMLButtonElement>,
    MarginProps<Theme>,
    TextAlignProps<Theme>,
    FontWeightProps<Theme>,
    VariantProps,
    SizeVariationProps {
  block?: boolean;
  disabled?: boolean;
  skin?: Skin;
  css?: any;
}

const COMPONENT_NAME = "Button";

const classes = {
  startAdornment: COMPONENT_NAME + "__startAdornment",
  endAdornment: COMPONENT_NAME + "__endAdornment",
};

const ButtonBase = styled("button", {
  shouldForwardProp: (name) => shouldForwardProp(name) && name !== "color",
})<ButtonBaseProps>`
  cursor: ${({ disabled }) => (disabled ? "not-allowed" : "pointer")};
  min-width: 36px;
  display: flex;
  gap: ${({ size }) => (size === "md" ? "10px" : "5px")};
  text-align: center;
  align-items: center;
  position: relative;
  font-size: 14px;
  font-weight: 400;
  line-height: 1.25;
  background: transparent;
  ${textAlign};
  ${variant({
    variants: buttonTheme.variants,
  })}
  ${(props) =>
    variant({
      prop: "size",
      variants: buttonTheme.sizes,
    })(props)}
    ${getButtonColorStyles}
    ${fontWeight}
    ${({ block }) =>
    block && {
      width: "100%",
    }}
`;

export type ButtonProps = ButtonBaseProps & {
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
  isLoading?: boolean;
  isPending?: boolean;
  pendingIndicatorPosition?: "start" | "end";
  count?: number;
};

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(
  (props, ref) => {
    const {
      type = "button",
      children,
      startAdornment,
      isLoading = false,
      isPending = false,
      count,
      disabled = false,
      size = "md",
      pendingIndicatorPosition = "start",
      endAdornment,
      className,
      variant = "outlined",
      skin = "info",
      ...rest
    } = props;

    const showPendingIndicatorEnd =
      isPending && pendingIndicatorPosition === "end";

    const showPendingIndicatorStart =
      isPending && pendingIndicatorPosition === "start";

    return (
      <Skeleton className={className} isLoading={isLoading}>
        <ButtonBase
          {...rest}
          skin={skin}
          variant={variant}
          type={type}
          className={className}
          ref={ref}
          size={size}
          disabled={isLoading || disabled}
        >
          {(showPendingIndicatorStart || startAdornment) && (
            <div className={classes.startAdornment} css={adornmentStyles}>
              {showPendingIndicatorStart ? <Spinner /> : startAdornment}
            </div>
          )}

          <Text>{children}</Text>

          {(showPendingIndicatorEnd || endAdornment) && (
            <div className={classes.endAdornment} css={adornmentStyles}>
              {showPendingIndicatorEnd ? <Spinner /> : endAdornment}
            </div>
          )}
          {count != null && (
            <div
              css={countStyles({
                skin,
              })}
            >
              <TextV2 variant={"body2"}>{count}</TextV2>
            </div>
          )}
        </ButtonBase>
      </Skeleton>
    );
  },
);

const Text = styled.span`
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  min-width: 0;
  flex-shrink: 1;
  flex-grow: 1;
`;

const adornmentStyles = css`
  height: 15px;
  flex: 0;
`;

const countStyles =
  ({ skin }: { skin: Skin }) =>
  (theme: ExactTheme) => {
    const colorName = (
      {
        info: "blue",
        success: "green",
        danger: "red",
        warning: "orange",
        neutral: "grey",
      } as const
    )[skin];
    return css({
      position: "absolute",
      top: -8,
      right: -1,
      padding: "0 5px",

      display: "flex",
      alignItems: "center",
      height: "17px",
      backgroundColor: "white",
      borderRadius: "10px",
      border: `1px solid ${theme.colorsV2[colorName][500]}`,
    });
  };
